// backgroundedAudioWorklet — CU-869dafa5q.
//
// Wraps the remote-track playback path so the jitter-buffer-draining audio
// pipeline runs on the AudioWorklet render thread instead of the document's
// main thread. Chrome's main-thread throttling on hidden tabs is the cause of
// the "chopping near end of call" reports we see from users (notably on
// Chrome OS) who move the call tab to the background.
//
// Topology:
//
//   remoteStream  ─▶ MediaStreamAudioSourceNode
//                 ─▶ AudioWorkletNode (passthrough — see passthroughAudioWorklet.js)
//                 ─▶ MediaStreamAudioDestinationNode
//                 ─▶ destination.stream  ─▶ HTMLAudioElement.srcObject
//                                          (preserves setSinkId compatibility)
//
// Every state transition emits an `audio_worklet_*` log event through the
// shared logger so trace queries in Kibana can confirm the worklet path ran
// for a given call.
//
// All public functions are no-ops on browsers that lack AudioContext or
// AudioWorklet. The caller (sipService) falls back to the c-level direct
// `audioElement.srcObject = remoteStream` path on those browsers.

import logger from "../services/logger";
import {bumpDiagCounter, isAudioDiagEnabled, diagLogInfo, diagLogWarn} from "./audioDiagCounters";

const WORKLET_MODULE_URL = '/passthroughAudioWorklet.js';
const WORKLET_PROCESSOR_NAME = 'phq-passthrough';

type OutputMode = 'element' | 'ctx-destination';

interface ActiveGraph {
    audioContext: AudioContext;
    sourceNode: MediaStreamAudioSourceNode;
    workletNode: AudioWorkletNode;
    outputMode: OutputMode;
    // 'element' mode: worklet → destinationNode → audioElement.srcObject.
    destinationNode: MediaStreamAudioDestinationNode | null;
    workletStream: MediaStream | null;
    // 'ctx-destination' mode: worklet → gainNode → ctx.destination. Volume
    // mirrors audioElement.volume via a volumechange listener; the element
    // itself is force-muted so its silent playback doesn't claim the page's
    // audio output slot away from ctx.destination on Chrome OS.
    gainNode: GainNode | null;
    audioElement: HTMLAudioElement | null;
    volumeListener: (() => void) | null;
    priorElementMuted: boolean | null;
}

export function needsRemoteStreamAnchor(): boolean {
    try {
        if (typeof navigator === 'undefined') return false;
        const ua = navigator.userAgent;
        if (/CrOS/i.test(ua)) return true;
        return /Mac OS X|Macintosh/i.test(ua) && /Chrome|Chromium|Edg|OPR/i.test(ua);
    } catch {
        return false;
    }
}

function isMacChromium(): boolean {
    try {
        if (typeof navigator === 'undefined') return false;
        const ua = navigator.userAgent;
        return /Mac OS X|Macintosh/i.test(ua) && /Chrome|Chromium|Edg|OPR/i.test(ua);
    } catch {
        return false;
    }
}

// One audio graph per HTMLAudioElement, keyed by element identity.
const graphs = new WeakMap<HTMLAudioElement, ActiveGraph>();

// AudioContext is expensive to construct; share one across calls.
let sharedContext: AudioContext | null = null;
let workletReadyPromise: Promise<void> | null = null;
// Guards late rejections from a discarded context's addModule from clobbering
// the new context's cached promise.
let workletGeneration = 0;

// Codex review — the original module called ctx.resume() once at attach
// time. AudioContext can drop back to "suspended" after a visibility flip,
// after a tab freeze/resume cycle, or after the audio backend reconfigures
// (device-change, sink-change). When that happens the worklet thread runs
// but the rendered output is muted, which looks identical to "main thread
// throttled the playout pump" from the user's perspective.
//
// We install `visibilitychange` / `pageshow` / `focus` + `ctx.statechange`
// listeners while any call's worklet graph is attached, and re-resume() on
// any event that could have suspended the context. Refcounted so multiple
// concurrent attaches share one listener set; teardown removes the
// listeners when the last graph detaches.
let keepRunningCleanup: (() => void) | null = null;
let activeGraphCount = 0;
// Wall-clock ms at which the most recent `freeze` event fired, or null if
// the page isn't currently frozen. Used to compute freezeDurationMs on the
// matching `resume`. The Page Lifecycle API guarantees freeze/resume pair
// up, but a discarded page never gets `resume` — we read wasDiscarded on
// the next page load instead.
let lastFreezeWallMs: number | null = null;

/**
 * Returns the shared AudioContext IFF a worklet graph is currently attached
 * for an active call. Codex review — `sharedContext` outlives any single
 * call (AudioContext is expensive to construct, so we reuse it). If
 * `getActiveAudioContext()` returned it unconditionally, a fallback-path
 * call following a worklet-routed call would see the stale suspended
 * context, and `audioDiagTick`'s stall classifier would (correctly!)
 * believe the worklet context is unhealthy and suppress stall events that
 * actually matter on the fallback path. Gating on activeGraphCount makes
 * "no worklet graph currently attached" indistinguishable from "browser
 * doesn't support worklet" for the diag-tick consumer.
 */
export function getActiveAudioContext(): AudioContext | null {
    if (activeGraphCount === 0) return null;
    return sharedContext;
}

async function ensureRunning(callId?: string): Promise<void> {
    const ctx = sharedContext;
    if (!ctx) return;
    if (ctx.state === 'running') return;
    try {
        await ctx.resume();
        bumpDiagCounter('workletContextResume');
        // Codex review — context.resume() is CORE behavior (we want the
        // pump running regardless of diagnostics state) but the LOG of
        // it is diagnostic. Wrap with diagLog so kill switch suppresses
        // the log without disabling the resume call above.
        diagLogInfo(logger, 'AudioWorklet: AudioContext resumed', {
            source: 'sip', event: 'audio_worklet_context_resumed',
            callId, contextState: ctx.state,
        });
    } catch (err) {
        diagLogWarn(logger, 'AudioWorklet: AudioContext resume failed', {
            source: 'sip', event: 'audio_worklet_context_resume_failed',
            callId, contextState: ctx.state, errorMessage: (err as Error)?.message,
        });
    }
}

function installEnsureRunningHooks(callId: string): void {
    if (keepRunningCleanup) return;
    // Capture the ctx the listener is bound to so cleanup removes the listener
    // from the same ctx, even if sharedContext was swapped in the meantime.
    const ctxAtInstall = sharedContext;
    const onVisibility = () => { void ensureRunning(callId); };
    const onPageShow = (e: PageTransitionEvent) => {
        // Codex review — distinguish bfcache restore from a plain visibility
        // flip. `persisted=true` means the document came back from bfcache;
        // `wasDiscarded` would have been logged on the previous page load.
        // Log is diagnostic, but the ensureRunning() call below is CORE.
        diagLogInfo(logger, 'AudioWorklet: pageshow', {
            source: 'sip', event: 'audio_page_show',
            callId, persisted: e.persisted,
            wasDiscarded: (document as Document & {wasDiscarded?: boolean}).wasDiscarded,
            contextStateBefore: sharedContext?.state,
            visibilityState: document.visibilityState,
        });
        void ensureRunning(callId);
    };
    const onFocus = () => { void ensureRunning(callId); };
    const onStateChange = () => {
        diagLogInfo(logger, 'AudioWorklet: AudioContext state changed', {
            source: 'sip', event: 'audio_worklet_context_state_change',
            callId, contextState: sharedContext?.state,
        });
        if (sharedContext?.state !== 'running') {
            void ensureRunning(callId);
        }
    };
    // Page Lifecycle API — `freeze`/`resume` fire when a hidden tab is
    // promoted to/from the "frozen" state. Chrome explicitly says hidden
    // tabs can become frozen or discarded; on resume we must re-ensure the
    // audio graph is running because the context may have been suspended
    // while frozen.
    const onFreeze = () => {
        lastFreezeWallMs = Date.now();
        bumpDiagCounter('pageFreeze');
        diagLogInfo(logger, 'AudioWorklet: page freeze', {
            source: 'sip', event: 'audio_page_freeze',
            callId, tsWallMs: lastFreezeWallMs,
            visibilityState: document.visibilityState,
            contextState: sharedContext?.state,
        });
    };
    const onResume = () => {
        const tsWallMs = Date.now();
        const freezeDurationMs = lastFreezeWallMs != null ? tsWallMs - lastFreezeWallMs : null;
        lastFreezeWallMs = null;
        const contextStateBefore = sharedContext?.state;
        bumpDiagCounter('pageResume');
        diagLogInfo(logger, 'AudioWorklet: page resume', {
            source: 'sip', event: 'audio_page_resume',
            callId, tsWallMs, freezeDurationMs,
            visibilityState: document.visibilityState,
            contextStateBefore,
        });
        // ensureRunning() logs its own audio_worklet_context_resumed (diag-
        // gated) if it actually had to resume.
        void ensureRunning(callId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onResume);
    ctxAtInstall?.addEventListener('statechange', onStateChange);
    keepRunningCleanup = () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pageshow', onPageShow);
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('freeze', onFreeze);
        document.removeEventListener('resume', onResume);
        ctxAtInstall?.removeEventListener('statechange', onStateChange);
        keepRunningCleanup = null;
    };
}

// Worklet heartbeat receiver — wired in attachWorkletPath, torn down by
// detachWorkletPath via the node's own `port.onmessage` reference being
// dropped when the AudioWorkletNode is GC'd. We also explicitly null it
// out on detach so a stuck reference can't keep firing.
//
// Codex review (round 2) — the heartbeat is the only signal that proves
// the audio render thread is alive. Without it, contextState='running'
// and a non-paused audio element can still produce silence if the worklet
// is wedged. The processor posts a heartbeat ~every 500 ms; we declare a
// gap after WORKLET_HEARTBEAT_GAP_MS without one.
const WORKLET_HEARTBEAT_EXPECTED_MS = 500;
const WORKLET_HEARTBEAT_GAP_MS = 1500;

interface HeartbeatState {
    seq: number;
    callId: string;
    lastReceivedWallMs: number;
    lastReceivedPerfMs: number;
    lastProcessorPerfMs: number | null;
    gapWatchdog: ReturnType<typeof setInterval> | null;
    inGap: boolean;
    gapStartWallMs: number | null;
    gapStartSeq: number | null;
    totalFrames: number;
    zeroOutputFrames: number;
    sampleRate: number | null;
}
const heartbeatStates = new WeakMap<AudioWorkletNode, HeartbeatState>();
let lastHeartbeatWallMs: number | null = null;

export function getLastWorkletHeartbeatWallMs(): number | null {
    return lastHeartbeatWallMs;
}

interface HeartbeatMessage {
    type?: string;
    seq?: number;
    processorPerfNowMs?: number | null;
    currentTimeSec?: number;
    currentFrame?: number;
    sampleRate?: number;
    totalFrames?: number;
    zeroOutputFrames?: number;
    inputChannelCount?: number;
    outputChannelCount?: number;
    inRms?: number;
    inAbsMax?: number;
    outRms?: number;
    outAbsMax?: number;
}

function installHeartbeatListener(node: AudioWorkletNode, callId: string): void {
    // Kill switch — the heartbeat listener installs a setInterval
    // watchdog and a postMessage receiver. Skip both when diagnostics
    // are off. The processor itself still posts heartbeats (cheap),
    // but with no listener they're dropped on the floor.
    if (!isAudioDiagEnabled()) return;
    const state: HeartbeatState = {
        seq: 0, callId,
        lastReceivedWallMs: Date.now(), lastReceivedPerfMs: performance.now(),
        lastProcessorPerfMs: null,
        gapWatchdog: null, inGap: false,
        gapStartWallMs: null, gapStartSeq: null,
        totalFrames: 0, zeroOutputFrames: 0, sampleRate: null,
    };
    heartbeatStates.set(node, state);

    node.port.onmessage = (event: MessageEvent<HeartbeatMessage>) => {
        const data = event.data;
        if (!data || data.type !== 'heartbeat') return;
        const wallMs = Date.now();
        const perfMs = performance.now();
        const oneWayMs = data.processorPerfNowMs != null ? perfMs - data.processorPerfNowMs : null;
        const prevSeq = state.seq;
        state.seq = data.seq ?? state.seq + 1;
        state.lastReceivedWallMs = wallMs;
        state.lastReceivedPerfMs = perfMs;
        state.lastProcessorPerfMs = data.processorPerfNowMs ?? null;
        const prevTotalFrames = state.totalFrames;
        const prevZeroOutputFrames = state.zeroOutputFrames;
        state.totalFrames = data.totalFrames ?? state.totalFrames;
        state.zeroOutputFrames = data.zeroOutputFrames ?? state.zeroOutputFrames;
        state.sampleRate = data.sampleRate ?? state.sampleRate;
        lastHeartbeatWallMs = wallMs;

        // Sample-flow log — answers "is the worklet actually receiving audio
        // samples from the source node, or just outputting zero-fill?".
        // Critical for diagnosing Chrome OS silence where contextState,
        // gain, volume, outputMode all look correct but no audio is heard.
        // Log first 3 heartbeats and every 10th thereafter to bound volume.
        const framesDelta = state.totalFrames - prevTotalFrames;
        const zeroDelta = state.zeroOutputFrames - prevZeroOutputFrames;
        if (state.seq <= 3 || state.seq % 10 === 0) {
            logger.info('AudioWorklet: heartbeat sample', {
                source: 'sip', event: 'audio_worklet_heartbeat',
                callId: state.callId, seq: state.seq,
                totalFrames: state.totalFrames, zeroOutputFrames: state.zeroOutputFrames,
                framesDelta, zeroDelta, sampleRate: state.sampleRate,
                inputChannelCount: data.inputChannelCount,
                outputChannelCount: data.outputChannelCount,
                inRms: data.inRms, inAbsMax: data.inAbsMax,
                outRms: data.outRms, outAbsMax: data.outAbsMax,
                contextState: sharedContext?.state,
                visibilityState: document.visibilityState,
            });
        }

        if (state.inGap) {
            const gapMs = state.gapStartWallMs != null ? wallMs - state.gapStartWallMs : null;
            logger.warn('AudioWorklet: heartbeat resumed', {
                source: 'sip', event: 'audio_worklet_heartbeat_resumed',
                callId: state.callId, gapMs,
                gapStartSeq: state.gapStartSeq, currentSeq: state.seq,
                contextState: sharedContext?.state,
                visibilityState: document.visibilityState,
            });
            state.inGap = false;
            state.gapStartWallMs = null;
            state.gapStartSeq = null;
        }

        // Skip-detection — if seq jumped by more than 1, render thread
        // dropped at least one heartbeat without us noticing a gap (rare,
        // but possible if the message queue was drained late).
        const skipCount = prevSeq > 0 ? state.seq - prevSeq - 1 : 0;
        if (skipCount > 0) {
            bumpDiagCounter('workletHeartbeatSeqSkip');
            logger.warn('AudioWorklet: heartbeat seq skip', {
                source: 'sip', event: 'audio_worklet_heartbeat_seq_skip',
                callId: state.callId,
                prevSeq, currentSeq: state.seq, skipCount,
                oneWayMs, contextState: sharedContext?.state,
            });
        }
    };

    // Watchdog runs on the main thread — yes, it can ALSO be throttled in
    // a bg tab, so a 'gap' reported here might be a watchdog-throttling
    // false positive. The audio_main_thread_timer_gap event (added in a
    // sibling commit) distinguishes the two by comparing wallDelta vs
    // expected interval. For this watchdog we accept the false-positive
    // rate as the cost of the signal being independent of the diag tick.
    const tick = () => {
        const now = Date.now();
        const sinceLastHbMs = now - state.lastReceivedWallMs;
        if (!state.inGap && sinceLastHbMs > WORKLET_HEARTBEAT_GAP_MS) {
            state.inGap = true;
            state.gapStartWallMs = state.lastReceivedWallMs;
            state.gapStartSeq = state.seq;
            bumpDiagCounter('workletHeartbeatGap');
            logger.warn('AudioWorklet: heartbeat gap', {
                source: 'sip', event: 'audio_worklet_heartbeat_gap',
                callId: state.callId,
                gapMs: sinceLastHbMs,
                expectedIntervalMs: WORKLET_HEARTBEAT_EXPECTED_MS,
                lastHeartbeatSeq: state.seq,
                lastHeartbeatWallMs: state.lastReceivedWallMs,
                lastHeartbeatPerfMs: state.lastReceivedPerfMs,
                contextState: sharedContext?.state,
                visibilityState: document.visibilityState,
            });
        }
    };
    state.gapWatchdog = setInterval(tick, 1000);

    node.addEventListener('processorerror', (e: Event) => {
        bumpDiagCounter('workletProcessorError');
        logger.warn('AudioWorklet: processor error', {
            source: 'sip', event: 'audio_worklet_processor_error',
            callId: state.callId,
            errorType: (e as ErrorEvent)?.type,
            message: (e as ErrorEvent)?.message,
            lastHeartbeatSeq: state.seq,
            lastHeartbeatAgeMs: Date.now() - state.lastReceivedWallMs,
            moduleUrl: WORKLET_MODULE_URL,
        });
    });

    logger.info('AudioWorklet: heartbeat listener installed', {
        source: 'sip', event: 'audio_worklet_heartbeat_installed',
        callId,
    });
}

function teardownHeartbeatListener(node: AudioWorkletNode): void {
    const state = heartbeatStates.get(node);
    if (!state) return;
    if (state.gapWatchdog) clearInterval(state.gapWatchdog);
    node.port.onmessage = null;
    heartbeatStates.delete(node);
}

// One-time `wasDiscarded` check. Fires at module import on the page load
// FOLLOWING a discard — i.e., the previous instance of this tab was killed
// by Chrome while frozen. That's a hard explanation for "call abruptly
// stopped working": the page was thrown away, and any rendezvous with the
// peer needs to re-establish from scratch. Gated to avoid logging on the
// happy boot path. No callId here — the call this would correlate to is
// on the PREVIOUS document; investigators correlate by tab/user.
let pageDiscardChecked = false;
function checkPageDiscardOnce(): void {
    if (pageDiscardChecked) return;
    pageDiscardChecked = true;
    const wasDiscarded = (document as Document & {wasDiscarded?: boolean}).wasDiscarded;
    if (wasDiscarded) {
        diagLogWarn(logger, 'AudioWorklet: page discarded boot', {
            source: 'sip', event: 'audio_page_discarded_boot',
            wasDiscarded: true,
            tsWallMs: Date.now(),
        });
    }
}
checkPageDiscardOnce();

function getOrCreateContext(): AudioContext | null {
    // After bfcache restore the previous context can come back 'closed'.
    // Reusing it makes resume()/addModule() reject; recreate instead. Run
    // keepRunningCleanup first so installEnsureRunningHooks rebinds against
    // the new ctx, and zero activeGraphCount so getActiveAudioContext()
    // stops returning the new ctx for graphs that died with the old one.
    if (sharedContext && sharedContext.state === 'closed') {
        logger.warn('AudioWorklet: discarding closed AudioContext', {
            source: 'sip', event: 'audio_worklet_context_closed_discard',
            generation: workletGeneration,
        });
        try { keepRunningCleanup?.(); } catch { /* best-effort */ }
        const dead = sharedContext;
        sharedContext = null;
        workletReadyPromise = null;
        activeGraphCount = 0;
        workletGeneration++;
        dead.close().catch(() => { /* best-effort — already closed */ });
    }
    if (sharedContext) return sharedContext;
    const Ctx = (window.AudioContext ?? (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext);
    if (!Ctx) return null;
    try {
        sharedContext = new Ctx({latencyHint: 'interactive'});
    } catch (err) {
        logger.warn('AudioWorklet: failed to construct AudioContext', {
            source: 'sip', event: 'audio_worklet_context_failed',
            errorMessage: (err as Error)?.message,
        });
        sharedContext = null;
    }
    return sharedContext;
}

async function ensureWorkletLoaded(ctx: AudioContext): Promise<void> {
    if (workletReadyPromise) return workletReadyPromise;
    const gen = workletGeneration;
    workletReadyPromise = ctx.audioWorklet.addModule(WORKLET_MODULE_URL).then(
        () => {
            logger.info('AudioWorklet: passthrough module loaded', {
                source: 'sip', event: 'audio_worklet_module_loaded',
                url: WORKLET_MODULE_URL, generation: gen,
            });
        },
        (err) => {
            // Don't null a generation we no longer own — a stale rejection
            // would clobber the new context's in-flight addModule.
            if (workletGeneration === gen) {
                workletReadyPromise = null;
            }
            logger.warn('AudioWorklet: addModule() failed', {
                source: 'sip', event: 'audio_worklet_module_failed',
                url: WORKLET_MODULE_URL, generation: gen,
                currentGeneration: workletGeneration,
                errorMessage: (err as Error)?.message,
            });
            throw err;
        },
    );
    return workletReadyPromise;
}

export function isBackgroundedAudioWorkletSupported(): boolean {
    const Ctx = window.AudioContext ?? (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
    if (!Ctx) return false;
    // `in` checks the property descriptor on the prototype without invoking
    // the getter — `typeof Ctx.prototype.audioWorklet` would call the getter
    // with `this === Ctx.prototype` and throw TypeError: Illegal invocation.
    return (
        'audioWorklet' in Ctx.prototype
        && 'MediaStreamAudioSourceNode' in window
        && 'MediaStreamAudioDestinationNode' in window
    );
}

/**
 * Route `remoteStream` through the passthrough AudioWorklet and attach the
 * resulting MediaStream to `audioElement.srcObject`. Returns true on success;
 * false means the caller should fall back to the direct-srcObject path.
 *
 * Idempotent across re-INVITE / ICE restart events: any previous graph
 * attached to the same element is torn down before a new one is wired.
 *
 * `isCancelled`: checked across each await and immediately before the
 * srcObject commit so a late-resolving attach can't clobber a newer call's
 * audio routing on the module-global `audioRef`.
 */
export async function attachWorkletPath(
    audioElement: HTMLAudioElement,
    remoteStream: MediaStream,
    callId: string,
    isCancelled: () => boolean = () => false,
): Promise<boolean> {
    if (!isBackgroundedAudioWorkletSupported()) {
        logger.info('AudioWorklet: not supported, skipping', {
            source: 'sip', event: 'audio_worklet_unsupported', callId,
        });
        return false;
    }
    detachWorkletPath(audioElement, callId);

    const ctx = getOrCreateContext();
    if (!ctx) return false;
    try {
        await ensureRunning(callId);
    } catch {
        // resume() pre-user-gesture is tolerated; ensureRunning() logs internally
    }
    if (isCancelled()) {
        logger.info('AudioWorklet: attach cancelled after resume', {
            source: 'sip', event: 'audio_worklet_attach_cancelled',
            callId, stage: 'after_resume',
        });
        return false;
    }
    try {
        await ensureWorkletLoaded(ctx);
    } catch {
        return false;
    }
    if (isCancelled()) {
        logger.info('AudioWorklet: attach cancelled after addModule', {
            source: 'sip', event: 'audio_worklet_attach_cancelled',
            callId, stage: 'after_add_module',
        });
        return false;
    }
    // Function-scope so catch can release them if construction throws
    // mid-wiring — an orphaned source node holds Chromium's claim on the
    // remote track and silently mutes any later element-srcObject fallback.
    let sourceNode: MediaStreamAudioSourceNode | null = null;
    let workletNode: AudioWorkletNode | null = null;
    let destinationNode: MediaStreamAudioDestinationNode | null = null;
    let gainNode: GainNode | null = null;
    try {
        // Chrome OS: per Chromium issue 40637961, remote WebRTC streams need a
        // hardware/media-element sink in addition to the Web Audio sink before
        // ctx.destination produces audible output. Anchor the *remoteStream*
        // (not destinationNode.stream) on the element as a muted reference so
        // play() resolves and the WebRTC playout pipeline is activated; the
        // worklet feeds ctx.destination for the actual audible path. No
        // MediaStreamAudioDestinationNode in this mode — it was the wrong
        // anchor.
        //
        // Other browsers keep the original element-fed path so setSinkId on
        // the element continues to drive output-device selection.
        const outputMode: OutputMode = needsRemoteStreamAnchor() ? 'ctx-destination' : 'element';
        const outputBoost = isMacChromium() ? 10 : 1;
        sourceNode = new MediaStreamAudioSourceNode(ctx, {mediaStream: remoteStream});
        workletNode = new AudioWorkletNode(ctx, WORKLET_PROCESSOR_NAME);
        if (outputMode === 'ctx-destination') {
            const initialGain = audioElement.muted ? 0 : audioElement.volume * outputBoost;
            gainNode = new GainNode(ctx, {gain: initialGain});
            sourceNode.connect(workletNode).connect(gainNode).connect(ctx.destination);
        } else {
            destinationNode = new MediaStreamAudioDestinationNode(ctx);
            sourceNode.connect(workletNode).connect(destinationNode);
        }
        // Last bail-out before mutating the module-global srcObject.
        if (isCancelled()) {
            sourceNode.disconnect();
            workletNode.disconnect();
            destinationNode?.disconnect();
            destinationNode?.stream.getTracks().forEach(t => t.stop());
            gainNode?.disconnect();
            logger.info('AudioWorklet: attach cancelled at commit', {
                source: 'sip', event: 'audio_worklet_attach_cancelled',
                callId, stage: 'pre_commit',
            });
            return false;
        }
        let volumeListener: (() => void) | null = null;
        let priorElementMuted: boolean | null = null;
        if (outputMode === 'ctx-destination' && gainNode) {
            const gn = gainNode;
            volumeListener = () => { gn.gain.value = audioElement.volume * outputBoost; };
            audioElement.addEventListener('volumechange', volumeListener);
            priorElementMuted = audioElement.muted;
            audioElement.muted = true;
            // Anchor the ORIGINAL remote WebRTC stream on the element (not
            // destinationNode.stream) — Chromium needs this for the audio
            // hardware sink it requires alongside ctx.destination.
            audioElement.srcObject = remoteStream;
        } else if (destinationNode) {
            audioElement.srcObject = destinationNode.stream;
        }
        graphs.set(audioElement, {
            audioContext: ctx, sourceNode, workletNode, outputMode,
            destinationNode, workletStream: destinationNode?.stream ?? null,
            gainNode, audioElement, volumeListener, priorElementMuted,
        });
        activeGraphCount++;
        installEnsureRunningHooks(callId);
        installHeartbeatListener(workletNode, callId);
        logger.info('AudioWorklet: receive path attached', {
            source: 'sip', event: 'audio_worklet_attached',
            callId, contextState: ctx.state, sampleRate: ctx.sampleRate,
            outputMode, activeGraphCount,
            elementVolume: audioElement.volume,
            elementMuted: audioElement.muted,
            priorElementMuted,
            gainValue: gainNode?.gain.value ?? null,
            outputBoost,
            remoteStreamAnchored: outputMode === 'ctx-destination',
            sinkIdSupported: typeof audioElement.setSinkId !== 'undefined',
            elementSinkId: 'sinkId' in audioElement
                ? (audioElement as HTMLAudioElement & {sinkId?: string}).sinkId ?? null
                : null,
        });
        return true;
    } catch (err) {
        logger.warn('AudioWorklet: graph wiring failed', {
            source: 'sip', event: 'audio_worklet_wire_failed',
            callId, errorMessage: (err as Error)?.message,
        });
        try { sourceNode?.disconnect(); } catch { /* */ }
        try { workletNode?.disconnect(); } catch { /* */ }
        try { destinationNode?.disconnect(); } catch { /* */ }
        try { destinationNode?.stream.getTracks().forEach(t => t.stop()); } catch { /* */ }
        try { gainNode?.disconnect(); } catch { /* */ }
        detachWorkletPath(audioElement, callId);
        return false;
    }
}

/**
 * Synchronously start ctx.resume() + addModule() while the dial-click
 * user-activation is still fresh. Without it Chrome's autoplay policy
 * parks both calls. Fire-and-forget; idempotent.
 */
export function prewarmBackgroundedAudioWorklet(callId: string = 'prewarm'): void {
    if (!isBackgroundedAudioWorkletSupported()) return;
    const ctx = getOrCreateContext();
    if (!ctx) return;
    logger.info('AudioWorklet: prewarm started', {
        source: 'sip', event: 'audio_worklet_prewarm_started',
        callId, contextState: ctx.state,
    });
    if (ctx.state !== 'running') {
        ctx.resume().then(
            () => logger.info('AudioWorklet: prewarm resume ok', {
                source: 'sip', event: 'audio_worklet_prewarm_resume_ok',
                callId, contextState: ctx.state,
            }),
            (err) => logger.warn('AudioWorklet: prewarm resume failed', {
                source: 'sip', event: 'audio_worklet_prewarm_resume_failed',
                callId, contextState: ctx.state, errorMessage: (err as Error)?.message,
            }),
        );
    }
    // Kick off addModule without awaiting; it caches via workletReadyPromise.
    ensureWorkletLoaded(ctx).catch(() => {
        // ensureWorkletLoaded logs its own audio_worklet_module_failed
    });
}

/**
 * Disconnect the worklet graph for this element (if any) and free its nodes.
 * Idempotent. AudioContext is reused across calls, so it isn't closed here.
 */
export function detachWorkletPath(audioElement: HTMLAudioElement, callId: string): void {
    const g = graphs.get(audioElement);
    if (!g) return;
    try {
        teardownHeartbeatListener(g.workletNode);
    } catch { /* watchdog teardown best-effort */ }
    if (g.volumeListener && g.audioElement) {
        try {
            g.audioElement.removeEventListener('volumechange', g.volumeListener);
        } catch { /* best-effort */ }
    }
    if (g.audioElement && g.priorElementMuted !== null) {
        try { g.audioElement.muted = g.priorElementMuted; } catch { /* best-effort */ }
    }
    try {
        g.sourceNode.disconnect();
        g.workletNode.disconnect();
        g.destinationNode?.disconnect();
        g.workletStream?.getTracks().forEach(t => t.stop());
        g.gainNode?.disconnect();
    } catch {
        // graph was already torn down — swallow
    }
    graphs.delete(audioElement);
    activeGraphCount = Math.max(0, activeGraphCount - 1);
    if (activeGraphCount === 0 && keepRunningCleanup) {
        keepRunningCleanup();
    }
    logger.info('AudioWorklet: receive path detached', {
        source: 'sip', event: 'audio_worklet_detached', callId,
        activeGraphCount,
    });
}
